import {
  GKEditor,
  GKTextArea,
  GKTextField,
  Grid2,
  InfoItalicIcon,
  Typography,
} from "components";
import { Controller, useFormContext } from "react-hook-form";
import { setFocusedField } from "store/slices/MyWebsiteSlice";
import { useAppDispatch } from "store/store";
import { htmlToText } from "utils/helpers";
import { emailRegex, validateNotWhitespaceOnly } from "utils/validationSchema";
import { WebsiteDetails } from "./types";

const FooterForm = () => {
  const form = useFormContext<WebsiteDetails>();

  const dispatch = useAppDispatch();

  const handleFocus = (name: string) => dispatch(setFocusedField(name));

  return (
    <Grid2 container spacing={3}>
      <Grid2
        container
        size={12}
        spacing={2}
        p={2}
        borderRadius={2}
        border={"1px solid var(--neutral-100)"}
      >
        <Grid2
          size={12}
          display={"flex"}
          alignItems={"center"}
          justifyContent={"space-between"}
        >
          <img
            src={form
              .getValues("websiteSection.myBranding.companyLogo")
              ?.toString()}
            alt=""
            height={24}
            width={"auto"}
          />
          <InfoItalicIcon />
        </Grid2>

        <Grid2 size={12}>
          <Typography fontSize={12} fontWeight={500} color="var(--neutral-500)">
            Firm Name
          </Typography>
          <Typography fontSize={14} fontWeight={500} color="var(--grey-900)">
            {htmlToText(
              form.getValues("websiteSection.heroSection.companyMetaName"),
            ) || "-"}
          </Typography>
        </Grid2>
        <Grid2 size={12}>
          <Typography fontSize={12} fontWeight={500} color="var(--neutral-500)">
            Full Name
          </Typography>
          <Typography fontSize={14} fontWeight={500} color="var(--grey-900)">
            {form.getValues("websiteSection.heroSection.advisorName") || "-"}
          </Typography>
        </Grid2>
        <Grid2 size={12}>
          <Typography fontSize={12} fontWeight={500} color="var(--neutral-500)">
            License Type
          </Typography>
          <Typography fontSize={14} fontWeight={500} color="var(--grey-900)">
            Registered Investment Advisor
          </Typography>
        </Grid2>
      </Grid2>

      <Grid2 size={12}>
        <Typography fontSize={14} fontWeight={700} color="var(--neutral-900)">
          SEBI Info
        </Typography>
      </Grid2>

      <Grid2 size={12}>
        <Controller
          name="websiteSection.footer.sebiRegistered"
          control={form.control}
          render={({ field, fieldState: { error } }) => (
            <GKTextField
              {...field}
              inputLabel="SEBI Registration Number"
              error={!!error}
              helperText={error?.message}
              onFocus={() => handleFocus(field.name)}
              onBlur={() => handleFocus(null)}
            />
          )}
        />
      </Grid2>

      <Grid2 size={12}>
        <Controller
          name="websiteSection.footer.sebiRegisteredSupervisoryBody"
          control={form.control}
          render={({ field }) => (
            <GKTextField
              {...field}
              inputLabel="SEBI Registered Supervisory Body"
              onFocus={() => handleFocus(field.name)}
              onBlur={() => handleFocus(null)}
            />
          )}
        />
      </Grid2>

      <Grid2 size={12}>
        <Controller
          name="websiteSection.footer.supervisoryBodyMembershipId"
          control={form.control}
          render={({ field }) => (
            <GKTextField
              {...field}
              inputLabel="Membership No."
              onFocus={() => handleFocus(field.name)}
              onBlur={() => handleFocus(null)}
            />
          )}
        />
      </Grid2>

      <Grid2 size={12}>
        <Typography fontSize={14} fontWeight={700} color="var(--neutral-900)">
          Footer Content
        </Typography>
      </Grid2>
      <Grid2 size={12}>
        <Controller
          name="websiteSection.footer.footerTagLine"
          control={form.control}
          rules={{
            required: {
              value: true,
              message: "Please enter company name",
            },
            validate: validateNotWhitespaceOnly,
          }}
          render={({ field, fieldState: { error } }) => {
            return (
              <GKEditor
                {...field}
                inputLabel="Footer Tagline"
                requiredField
                error={!!error}
                helperText={error?.message}
                minHeight={50}
                toolbar={[
                  "undo",
                  "redo",
                  "|",
                  "fontColor",
                  "fontBackgroundColor",
                ]}
                onFocus={() => handleFocus(field.name)}
                onBlur={() => handleFocus(null)}
              />
            );
          }}
        />
      </Grid2>
      <Grid2 size={12}>
        <Controller
          name="websiteSection.footer.footerTagLineBody"
          control={form.control}
          rules={{
            required: {
              value: true,
              message: "Please enter company name",
            },
            validate: validateNotWhitespaceOnly,
          }}
          render={({ field, fieldState: { error } }) => {
            return (
              <GKEditor
                {...field}
                inputLabel="Footer Body"
                requiredField
                error={!!error}
                helperText={error?.message}
                minHeight={50}
                toolbar={[
                  "undo",
                  "redo",
                  "|",
                  "fontColor",
                  "fontBackgroundColor",
                ]}
                onFocus={() => handleFocus(field.name)}
                onBlur={() => handleFocus(null)}
              />
            );
          }}
        />
      </Grid2>
      <Grid2 size={12}>
        <Typography fontSize={14} fontWeight={700} color="var(--neutral-900)">
          Contact Details
        </Typography>
      </Grid2>
      <Grid2 size={12}>
        <Controller
          name="websiteSection.footer.advisorPhone"
          control={form.control}
          render={({ field, fieldState: { error } }) => (
            <GKTextField
              {...field}
              type="number"
              inputLabel="Phone No."
              error={!!error}
              helperText={error?.message}
              onFocus={() => handleFocus(field.name)}
              onBlur={() => handleFocus(null)}
            />
          )}
        />
      </Grid2>
      <Grid2 size={12}>
        <Controller
          name="websiteSection.footer.advisorEmail"
          control={form.control}
          rules={{
            required: {
              value: true,
              message: "Please enter email",
            },
            pattern: {
              value: emailRegex,
              message: "Please enter a valid email",
            },
          }}
          render={({ field, fieldState: { error } }) => (
            <GKTextField
              {...field}
              inputLabel="Email Id"
              requiredField
              error={!!error}
              helperText={!!error && error?.message}
              onFocus={() => handleFocus(field.name)}
              onBlur={() => handleFocus(null)}
            />
          )}
        />
      </Grid2>
      <Grid2 size={12}>
        <Controller
          name="websiteSection.footer.advisorAddress"
          control={form.control}
          rules={{
            required: {
              value: true,
              message: "Please enter address",
            },
          }}
          render={({ field, fieldState: { error } }) => (
            <GKTextArea
              {...field}
              requiredField
              inputLabel="Address"
              rows={4}
              error={!!error}
              helperText={error?.message}
              onFocus={() => handleFocus(field.name)}
              onBlur={() => handleFocus(null)}
            />
          )}
        />
      </Grid2>
    </Grid2>
  );
};

export default FooterForm;
